import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminToken } from "@/lib/cms";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Relatieve /api/media-URL's absoluut maken voor de preview. */
export const mediaUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  if (url.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

/**
 * Afbeelding voor een CMS-blok: uploaden vanaf je computer of een URL plakken.
 * Uploads gaan naar de eigen backend (/api/beheer/media) en worden bewaard in MongoDB.
 */
export const ImageField = ({ label = "Afbeelding", value = "", onChange, testId = "block-image" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Afbeelding is groter dan 5 MB");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("bestand", file);
      const res = await fetch(`${BASE}/beheer/media`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken()}` },
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Uploaden mislukt");
      onChange(data.url);
      toast.success("Afbeelding geüpload");
    } catch (e) {
      toast.error(e.message || "Uploaden mislukt");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-secondary">
          {value ? (
            <img
              src={mediaUrl(value)}
              alt=""
              className="h-full w-full object-contain"
              data-testid={`${testId}-preview`}
            />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-[220px] flex-1 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid={`${testId}-upload-button`}
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {busy ? "Uploaden…" : "Afbeelding uploaden"}
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange("")}
                data-testid={`${testId}-remove-button`}
              >
                <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Verwijderen
              </Button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`${testId}-file-input`}
          />
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…of plak een afbeelding-URL"
            data-testid={`${testId}-url-input`}
          />
          <p className="text-xs text-muted-foreground">JPG, PNG, WEBP, GIF of SVG · max 5 MB</p>
        </div>
      </div>
    </div>
  );
};
